/**
 * The forty aerodromes of `AERO.DAT`, transcribed from the real file.
 *
 * Decoded here with the record layout documented in
 * docs/formats/missions-campaign.md § *`AERO.DAT`* (`0xFE` sentinel, `uint16le`
 * name-pool offset, side, secondary flag, map X, map Y, map id `0–4` or `0xFF`)
 * against `VOLUME.003/AERO.DAT` in both the floppy and CD installs — 40 records
 * of 10 bytes, `0xFD` terminator at `0x190`, 40 pooled strings.
 *
 * The **map id is the region**, and it lines up with `mission-world.ts`'s
 * `MISSION_REGIONS` in order: 0 London, 1 Dunkirk, 2 Somme, 3 Paris, 4 Verdun.
 * The English fields (Ashford, Hainault Farm, Rochford, Sutton's Farm) all
 * carry map id 0, which independently confirms docs/game/aces-squadrons.md §5's
 * reading of "London" as the home-defence region rather than a trench sector.
 * The `side` byte is 1 for Allied fields and 0 for German ones.
 *
 * **This is a stand-in for a bake stage**, the same posture `aircraft-table.ts`
 * takes: the values are a straight read of the shipped file, kept in source so
 * the campaign layer is testable before `AERO.DAT` has a bake stage of its own.
 */

import type { Nation } from "../mission/mission-roster.ts";
import type { MissionRegion } from "../mission/mission-world.ts";

export interface Aerodrome {
  readonly name: string;
  readonly region: MissionRegion;
  readonly nation: Nation;
  /** `AERO.DAT`'s map X/Y, in that file's own units. */
  readonly mapX: number;
  readonly mapY: number;
}

function field(name: string, region: MissionRegion, nation: Nation, mapX: number, mapY: number): Aerodrome {
  return { name, region, nation, mapX, mapY };
}

export const AERODROMES: readonly Aerodrome[] = [
  field("Ashford", "london", "allied", 40, 31),
  field("Bar-le-Duc", "verdun", "allied", 170, 133),
  field("Bertangles", "somme", "allied", 83, 81),
  field("Bertincourt", "somme", "allied", 100, 74),
  field("Cachy", "somme", "allied", 87, 88),
  field("Cambrai", "somme", "german", 112, 78),
  field("Cappy", "somme", "german", 103, 87),
  field("Chateau-Thierry", "paris", "allied", 123, 130),
  field("Courtrai", "dunkirk", "german", 112, 44),
  field("Douai", "somme", "german", 105, 64),
  field("Dunkirk", "dunkirk", "allied", 82, 41),
  field("Estrees St. Denis", "paris", "allied", 90, 110),
  field("Fismes", "paris", "allied", 127, 113),
  field("Furnes", "dunkirk", "allied", 90, 38),
  field("Hainault Farm", "london", "allied", 14, 7),
  field("Laon", "paris", "german", 125, 96),
  field("La Gorgue", "dunkirk", "allied", 96, 53),
  field("Le Plessis Belleville", "paris", "allied", 92, 122),
  field("Lille", "somme", "german", 104, 56),
  field("St. Marie Cappel", "dunkirk", "allied", 90, 45),
  field("Maxville", "verdun", "allied", 202, 138),
  field("Metz", "verdun", "german", 196, 118),
  field("Ostend", "dunkirk", "german", 97, 35),
  field("Rochford", "london", "allied", 38, 7),
  field("Sivry", "verdun", "german", 171, 107),
  field("Souilly", "verdun", "allied", 165, 123),
  field("St. Marguerite", "dunkirk", "german", 100, 40),
  field("St. Mihiel", "verdun", "german", 180, 121),
  field("St. Omer", "dunkirk", "allied", 85, 48),
  field("St. Pol", "somme", "allied", 90, 60),
  field("St. Quentin", "paris", "german", 111, 90),
  field("Sutton's Farm", "london", "allied", 21, 7),
  field("Tellancourt", "verdun", "german", 177, 104),
  field("Toul", "verdun", "allied", 185, 137),
  field("Vert Galant", "somme", "allied", 90, 76),
  field("Villers-Cotterets", "paris", "allied", 107, 117),
  field("Vousiers", "verdun", "german", 167, 103),
];

/**
 * The three records whose map id is `0xFF` — "not on a campaign map" — so they
 * have no region to be stationed in. Recorded rather than quietly dropped; the
 * file really does hold forty fields, and these are the other three.
 */
export const OFF_MAP_AERODROMES: readonly string[] = ["Ghent", "Pont-Faverger", "Chalons-sur-Marne"];

export function aerodromeByName(name: string): Aerodrome | undefined {
  return AERODROMES.find((entry) => entry.name === name);
}

export function requireAerodrome(name: string): Aerodrome {
  const found = aerodromeByName(name);
  if (!found) throw new Error(`no aerodrome "${name}" in AERO.DAT`);
  return found;
}

export function aerodromesIn(region: MissionRegion, nation: Nation): readonly Aerodrome[] {
  return AERODROMES.filter((entry) => entry.region === region && entry.nation === nation);
}
